import React from 'react';
import LegalLayout, { H2, H3, P, UL } from '@/components/LegalLayout';

export default function PrivacyPolicyPage() {
  return (
    <LegalLayout eyebrow="LEGAL" title="Privacy Policy" updated="June 2026">
      <P>
        Christovibes Radio ("Christovibes", "we", "our" or "us") is a 24-hour internet radio
        station broadcasting contemporary African Christian music. This Privacy Policy explains
        what personal information we collect when you listen, submit music, request a song, or
        share a prayer, how we use it, and the choices you have. Our goal is simple: to serve
        listeners and artists honestly, and to steward your information with care.
      </P>

      <H2>1. Information We Collect</H2>
      <H3>Information you provide</H3>
      <UL>
        <li><strong>Music submissions</strong>: artist/band name, email address, song title, optional notes, and the audio file you upload.</li>
        <li><strong>Song requests & dedications</strong>: song name, optional artist, your name (optional), email (optional), and message.</li>
        <li><strong>Prayer wall</strong>: your name (or "Anonymous"), and the prayer message you share publicly.</li>
        <li><strong>Admin accounts</strong>: for our internal team only — email and hashed password.</li>
      </UL>

      <H3>Information collected automatically</H3>
      <UL>
        <li><strong>Technical logs</strong>: IP address, user agent, timestamps, and pages viewed, retained for a short period for security and to prevent abuse (e.g. brute-force protection on admin login).</li>
        <li><strong>Listening telemetry</strong>: aggregated stream connection counts provided by our streaming partner (RadioBOSS/Icecast). This does not identify you.</li>
      </UL>

      <H2>2. How We Use Information</H2>
      <UL>
        <li>Deliver the live radio stream, schedule, shows and catch-up episodes.</li>
        <li>Review, curate and — if approved — broadcast the music you submit.</li>
        <li>Notify you by email about the status of your submission (received, rejected, or broadcast on air) using our email partner, Resend.</li>
        <li>Display your song request or prayer publicly (only if you posted it publicly).</li>
        <li>Protect the service against fraud, abuse and unauthorised access.</li>
      </UL>

      <H2>3. Legal Basis</H2>
      <P>
        Where the EU/UK GDPR applies we rely on: (a) your consent when you send us a submission,
        request or prayer; (b) our legitimate interest in keeping the service safe and functional;
        and (c) compliance with legal obligations (e.g. responding to lawful requests).
      </P>

      <H2>4. Sharing Your Information</H2>
      <P>
        We do not sell your data. We share limited information only with trusted service providers
        that make Christovibes possible:
      </P>
      <UL>
        <li><strong>Streaming</strong>: RadioBOSS / Icecast — to deliver the live audio stream.</li>
        <li><strong>Email</strong>: Resend — to send transactional emails about your submission.</li>
        <li><strong>Storage</strong>: Emergent Object Storage — to store your uploaded audio and show artwork.</li>
        <li><strong>AI assistance</strong>: OpenAI (GPT Image 1) — used only by administrators to generate show cover art; listener data is never sent to AI providers.</li>
      </UL>
      <P>We may disclose information if required by law, or to protect the rights and safety of Christovibes, our listeners, and our community.</P>

      <H2>5. Retention</H2>
      <UL>
        <li>Music submissions and their audio files are kept while your track is under review or on rotation, and archived up to 24 months thereafter for playback logs.</li>
        <li>Song requests and prayer wall posts remain public until you or an administrator remove them.</li>
        <li>Security logs are typically retained for 30–90 days.</li>
      </UL>

      <H2>6. Your Rights</H2>
      <UL>
        <li>Access, correct or delete the personal data we hold about you.</li>
        <li>Withdraw consent for a music submission or ask us to unpublish a public post.</li>
        <li>Object to processing or ask us to restrict how we use your data.</li>
        <li>Lodge a complaint with your local data-protection authority.</li>
      </UL>
      <P>To exercise these rights, email us at <a className="text-[#D4AF37] hover:underline" href="mailto:privacy@christovibes.com">privacy@christovibes.com</a>.</P>

      <H2>7. Children</H2>
      <P>
        Christovibes is intended for listeners aged 13 and above. If you believe a child has shared
        personal information with us without a parent's consent, please contact us and we will
        promptly remove it.
      </P>

      <H2>8. International Transfers</H2>
      <P>
        Christovibes serves a global African-Christian audience. Your data may be processed in
        countries outside your own, including where our service providers are based. We use
        providers that maintain appropriate safeguards.
      </P>

      <H2>9. Changes to this Policy</H2>
      <P>
        We may update this Privacy Policy occasionally. Material changes will be highlighted on
        this page with a revised "Last updated" date. Continued use of Christovibes after a change
        constitutes acceptance of the updated policy.
      </P>

      <H2>10. Contact</H2>
      <P>
        Questions? Reach out to <a className="text-[#D4AF37] hover:underline" href="mailto:privacy@christovibes.com">privacy@christovibes.com</a>.
        As part of our Christian ethos, we aim to respond promptly, humbly and honestly.
      </P>
    </LegalLayout>
  );
}
